import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorText } from "../lib/api";
import { fmtUSDT, todayNepal } from "../lib/partyBook";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

const emptyEntry = (partyId = "") => ({
  party_id: partyId,
  date: todayNepal(),
  direction: "sent",
  currency: "USDT",
  amount: "",
  rate_per_usdt: "",
  reference: "",
  notes: "",
});

const formFromEntry = (entry, fallbackPartyId) => ({
  party_id: entry.party_id || fallbackPartyId || "",
  date: entry.date || todayNepal(),
  direction: entry.direction || "sent",
  currency: entry.currency || "USDT",
  amount: String(entry.amount_original ?? entry.amount_usdt ?? ""),
  rate_per_usdt: entry.currency && entry.currency !== "USDT"
    ? String(entry.rate_per_usdt ?? "")
    : "",
  reference: entry.reference || "",
  notes: entry.notes || "",
});

export default function PartyEntryDialog({ open, onOpenChange, parties, initialPartyId, entry = null, onSaved }) {
  const [form, setForm] = useState(emptyEntry(initialPartyId));
  const [saving, setSaving] = useState(false);
  const defaultPartyId = initialPartyId || parties?.[0]?.id || "";

  useEffect(() => {
    if (open) setForm(entry ? formFromEntry(entry, defaultPartyId) : emptyEntry(defaultPartyId));
  }, [open, defaultPartyId, entry]);

  const calculatedUSDT = useMemo(() => {
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return 0;
    if (form.currency === "USDT") return amount;
    const rate = Number(form.rate_per_usdt);
    if (rate <= 0) return 0;
    return form.currency === "USD" ? amount / (rate / 100) : amount / rate;
  }, [form.amount, form.currency, form.rate_per_usdt]);

  const saveEntry = async () => {
    const amount = Number(form.amount);
    const rate = Number(form.rate_per_usdt);
    if (!form.party_id) return toast.error("Select a party");
    if (!amount || amount <= 0) return toast.error(`Enter a valid ${form.currency} amount`);
    if (form.currency !== "USDT" && (!rate || rate <= 0)) {
      return toast.error(form.currency === "USD" ? "Enter the USD rate percentage" : "Enter NPR per 1 USDT rate");
    }

    setSaving(true);
    try {
      const payload = {
        party_id: form.party_id,
        date: form.date,
        direction: form.direction,
        currency: form.currency,
        amount,
        rate_per_usdt: form.currency === "USDT" ? 1 : rate,
        reference: form.reference.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (entry?.id) {
        await api.put(`/party-book/entries/${entry.id}`, payload);
      } else {
        await api.post("/party-book/entries", payload);
      }
      toast.success(entry?.id ? "Entry updated" : form.direction === "sent" ? "Sent entry added" : "Received entry added");
      onOpenChange(false);
      await onSaved?.();
    } catch (error) {
      toast.error(apiErrorText(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">{entry?.id ? "Edit party entry" : "Add party entry"}</DialogTitle>
          <DialogDescription>
            Enter USDT directly, or enter USD/NPR with the rate. The ledger stores the calculated USDT.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Party *</Label>
            <Select value={form.party_id || "__none"} onValueChange={(value) => setForm({ ...form, party_id: value === "__none" ? "" : value })}>
              <SelectTrigger><SelectValue placeholder="Select party" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none" disabled>Select party</SelectItem>
                {(parties || []).map((party) => <SelectItem key={party.id} value={party.id}>{party.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Type *</Label>
            <Select value={form.direction} onValueChange={(value) => setForm({ ...form, direction: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="sent">Sent</SelectItem><SelectItem value="received">Received</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Date (Nepal) *</Label>
            <Input type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Currency *</Label>
            <Select value={form.currency} onValueChange={(value) => setForm({ ...form, currency: value, rate_per_usdt: value === "USDT" ? "" : form.rate_per_usdt })}>
              <SelectTrigger data-testid="party-entry-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USDT">USDT</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="NPR">NPR</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Amount {form.currency} *</Label>
            <Input type="number" min="0" step="0.0001" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} placeholder="0.0000" />
          </div>
          {form.currency !== "USDT" && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{form.currency === "USD" ? "Rate (%)" : "Rate (NPR per 1 USDT)"} *</Label>
              <Input type="number" min="0" step="0.000001" value={form.rate_per_usdt} onChange={(event) => setForm({ ...form, rate_per_usdt: event.target.value })} placeholder={form.currency === "NPR" ? "Example: 140" : "Example: 95"} />
              <p className="text-[11px] text-zinc-500">
                {form.currency === "USD"
                  ? "Example: 1,000 USDT × 95% = 950 USD · Formula: USD ÷ (rate ÷ 100) = USDT"
                  : "Formula: NPR amount ÷ rate = USDT"}
              </p>
            </div>
          )}
          <div className="rounded-md border hairline bg-sky-50/50 px-3 py-2.5 sm:col-span-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Calculated ledger amount</div>
            <div className="mt-1 font-mono text-[17px] font-medium text-zinc-900">{fmtUSDT(calculatedUSDT)}</div>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Reference</Label>
            <Input value={form.reference} onChange={(event) => setForm({ ...form, reference: event.target.value })} placeholder="Transaction ID, invoice, or reference" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Optional note" rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={saveEntry} disabled={saving} className="bg-zinc-900 text-white hover:bg-zinc-800">{saving ? "Saving..." : entry?.id ? "Update entry" : "Save entry"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
